import { getIn } from "final-form";
import {
  validateCompanyDocs,
  validateCompanyFields,
  validateGstDetails,
} from "../CompanyDetails/companyDetailsHelper";
import { validateDirectorFields } from "../Director/directorHelper";
import { investmentFormValidation } from "../Investments/investementHelper";
import { revenueFormValidation } from "../Revenue Details/revenueHelper";
import { processingStatus } from "../UploadDocuments/BankStatements";
import { ApplicationContextType, FinancialStatementClassificationStatus } from "../../../interfaces/interfaces";
import { bankStatementValidator } from "../../MonthlyReport/BankStatement/bankStatementHelper";
import _ from "lodash";
import { validateBusinessUpdateStep } from "../../MonthlyReport/BusinessUpdates/businessUpdate.helper";

export type ChecklistStatusTone = "success" | "error" | "pending";

export interface ChecklistStatusSegment {
  label: string;
  tone: ChecklistStatusTone;
}

const getConnectedCount = (account: any) =>
  (account?.accounts?.length ?? 0) + (account?.inProgressAccounts?.length ?? 0);

export const getAccountConnectionInfo = (
  accounts: any[],
  isOverridden = false
) => {
  const connectedCount = accounts.reduce(
    (prev, account) => prev + getConnectedCount(account),
    0
  );
  const disconnectedCount = accounts.reduce(
    (prev, account) => prev + (account?.disconnectedAccounts?.length ?? 0),
    0
  );
  // a suggested channel only counts while the user hasn't connected it
  const pendingCount = accounts.filter(
    (account) => account?.isSuggested && !getConnectedCount(account)
  ).length;
  const salesConnected = accounts.some(
    (account) => account?.isSalesChannel && getConnectedCount(account)
  );

  const segments: ChecklistStatusSegment[] = [];
  if (connectedCount) {
    segments.push({ label: `${connectedCount} connected`, tone: "success" });
  }
  if (disconnectedCount) {
    segments.push({
      label: `${disconnectedCount} disconnected`,
      tone: "error",
    });
  }
  if (pendingCount) {
    segments.push({ label: `${pendingCount} pending`, tone: "pending" });
  }

  return {
    successDetails: {
      message: `${connectedCount} accounts connected.`,
      segments,
    },
    // a disconnected channel blocks submit; a pending suggestion only nudges.
    // the dev override skips both checks so the step never blocks the form.
    isError: isOverridden
      ? false
      : !salesConnected || Boolean(disconnectedCount),
    disconnectedCount,
    pendingCount,
    errorDetails: {
      message: "No sales channel has been connected.",
      segments,
    },
  };
};

export const getBankStatementErrorInfo = (
  values,
  underwritingFileJobs,
  statementJobs,
  formType = ApplicationContextType.underwriting
) => {
  // const { allErrors } = bankStatementValidation(
  //   values,
  //   underwritingFileJobs,
  //   statementJobs
  // );
  const { allErrors } = bankStatementValidator(
    values,
    underwritingFileJobs,
    statementJobs,
    formType
  );

  const { rootError, statementError, passwordError } = allErrors;

  let isStatementLoading = false;
  // const processingStatus
  if (values?.bankStatements && values?.bankStatements?.length) {
    isStatementLoading = values?.bankStatements.some((statement) => {
      const statementId = statement?.statementJobId;
      const statementType = statement?.statementType;
      const statementJob = getIn(
        statementJobs,
        `${statementId}.${statementType}`
      );
      // console.log("statementJob", statementJob);
      if (statementJob && processingStatus.has(statementJob.status)) {
        return true;
      }
    });
  }

  console.log();

  if (isStatementLoading) {
    return {
      successDetails: {
        message: "Processing statement...",
      },
      isError: false,
      errorDetails: {
        message: "",
      },
      isLoading: true,
    };
  } else if (rootError.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Missing bank statements.",
      },
    };
  } else if (statementError.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Uploaded statement is invalid or couldn’t be processed.",
      },
    };
  } else if (passwordError.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Uploaded statement is password-protected.",
      },
    };
  } else {
    const bankStatementLength = values?.bankStatements?.length;
    return {
      successDetails: {
        message: bankStatementLength
          ? `${bankStatementLength} ${
              bankStatementLength?.length > 1 ? "accounts" : "account"
            } added.`
          : "",
      },
      isError: false,
      errorDetails: {
        message: "",
      },
    };
  }
};

export const getCompanyDetailsErrorInfo = (
  values,
  underwritingFileJobs,
  gstAnalysisJobs,
  formType
) => {
  const { missingData } = validateCompanyFields(values);
  const { missingDocs, requirePassword } = validateCompanyDocs(
    values,
    underwritingFileJobs
  );

  const { errors, allErrors } = validateGstDetails(
    values,
    underwritingFileJobs,
    {},
    gstAnalysisJobs,
    formType
  );

  console.log("gst All errors", allErrors);

  if (missingData.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Details are incomplete.",
      },
    };
  } else if (allErrors?.missingAccounts?.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "GST not connected.",
      },
    };
  } else if (missingDocs.length || allErrors?.missingDocs?.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Some documents are missing.",
      },
    };
  } else if (requirePassword.length || allErrors?.requirePassword?.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Some documents are password-protected.",
      },
    };
  } else if (allErrors?.isLoading) {
    return {
      successDetails: {
        message: "Verifying GST...",
      },
      isError: false,
      errorDetails: {
        message: "",
      },
      isLoading: true,
    };
  } else if (allErrors?.gstsUnverified?.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: `${allErrors?.gstsUnverified.length} GST(s) unverified.`,
      },
    };
  } else {
    return {
      successDetails: {
        message: "All complete.",
      },
      isError: false,
      errorDetails: {
        message: "",
      },
    };
  }
};

export const getDirectorDetailsErrorInfo = (values, underwritingFileJobs) => {
  const { missingData, requirePassword, missingDocs } = validateDirectorFields(
    values,
    underwritingFileJobs
  );

  if (missingData.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Details are incomplete.",
      },
    };
  } else if (missingDocs.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Some documents are missing.",
      },
    };
  } else if (requirePassword.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Some documents are password-protected.",
      },
    };
  } else {
    const directorLength = values?.directors?.length;
    return {
      successDetails: {
        message: `${directorLength} directors added.`,
      },
      isError: false,
      errorDetails: {
        message: "",
      },
    };
  }
};

export const getRevenueDetailsErrorInfo = (values, underwritingFileJobs, revenueClassificationJobs?) => {
  const { missingData, missingDocs, requirePassword } = revenueFormValidation(
    values,
    underwritingFileJobs
  );

  if (missingData.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Details are incomplete.",
      },
    };
  } else if (missingDocs.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Financials missing.",
      },
    };
  } else if (requirePassword.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Some documents are password-protected.",
      },
    };
  } else {
    const hasClassificationFailure = revenueClassificationJobs
      ? Object.values(revenueClassificationJobs).some(
          (job: any) => job?.status === FinancialStatementClassificationStatus.FAILURE
        )
      : false;

    if (hasClassificationFailure) {
      return {
        successDetails: {
          message: "",
        },
        isError: true,
        errorDetails: {
          message: "Uploaded financial is invalid or couldn't be processed.",
        },
      };
    }

    return {
      successDetails: {
        message: "All complete.",
      },
      isError: false,
      errorDetails: {
        message: "",
      },
    };
  }
};

export const getInvestmentDetailsErrorInfo = (values) => {
  const { missingData } = investmentFormValidation(values);

  if (missingData.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Details are incomplete.",
      },
    };
  } else {
    return {
      successDetails: {
        message: "All complete.",
      },
      isError: false,
      errorDetails: {
        message: "",
      },
    };
  }
};

export const getSalesMarketingErrorInfo = (values) => {
  const adSpendsData = _.get(values, "salesAndAdSpends.Ad Spends", []);
  const salesData = _.get(values, "salesAndAdSpends.Net Sales", []);

  return {
    successDetails: {
      message: `${salesData?.length} sales channels added. ${adSpendsData?.length} ad channels added.`,
    },
    isError: false,
    errorDetails: {
      message: "",
    },
  };
};

export const getBusinessUpdatesErrorInfo = (values, monthYear) => {
  const { allErrors } = validateBusinessUpdateStep(values, monthYear);

  if (allErrors?.missingData?.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "Details are incomplete.",
      },
    };
  } else {
    return {
      successDetails: {
        message: "All complete.",
      },
      isError: false,
      errorDetails: {
        message: "",
      },
    };
  }
};

export const getGstStepErrorInfo = (
  values,
  underwritingFileJobs,
  gstAnalysisJobs,
  formType
) => {
  const { allErrors } = validateGstDetails(
    values,
    underwritingFileJobs,
    {},
    gstAnalysisJobs,
    formType
  );

  const { missingDocs, gstsUnverified, requirePassword } = allErrors;

  if (gstsUnverified.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: `${gstsUnverified.length} GST(s) unverified.`,
      },
    };
  } else if (missingDocs.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "GST certificate(s) missing.",
      },
    };
  } else if (requirePassword.length) {
    return {
      successDetails: {
        message: "",
      },
      isError: true,
      errorDetails: {
        message: "GST certificate(s) are password-protected.",
      },
    };
  } else {
    const gstLength = values?.gstDetails?.length;
    return {
      successDetails: {
        message: `${gstLength} GSTs added.`,
      },
      isError: false,
      errorDetails: {
        message: "",
      },
    };
  }
};
